/* eslint-disable react/prop-types */
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { Table, notification, Select, Tooltip, Upload } from 'antd';
import { CaretDownOutlined, CaretUpOutlined } from '@ant-design/icons';
import { connect } from 'react-redux';
import { get } from 'lodash';
import { Button as CrceButton } from '@crc-evans/ui-components';
import {
  Eye,
  Pencil,
  Trash2,
  FileText,
  Download,
  Upload as UploadIcon,
  Plus,
  Truck,
  Search,
  X,
} from 'lucide-react';
import AddNewShipments from './AddNewShipments';
import ShipmentLogs from './ShipmentLogs';
import { fetchShipmentTypeDetails } from '../../redux/ShipmentDetails/actions';
import { fetchShipmentDashBoardDetails } from '../../redux/ShipmentDashboardDetails/actions';
import { OnDeleteShipments } from '../../redux/ShipmentDashboardDetails/saga';
import axiosConfig from '../../redux/ShipmentDashboardDetails/axiosConfig';
import axios from 'axios';
import { AWS_IMAGE_URL } from '../../config';
import {
  SHIPMENT_PRIORITY_OPTIONS,
  SHIPMENT_CATEGORY_OPTIONS,
  SHIPMENT_PACKAGING_OPTIONS,
  normalizeShipmentMetaFromRecord,
} from './shipmentFieldOptions';
import { getProjectTableLoadingConfig } from '../ProjectTableLoader';
import { ProjectTabTableEmpty } from '../project/ProjectTabTableEmpty';
import '../../pages/technical/HeatProfilePage.css';
import '../DPR/DPRSettingsPipelinesTab.css';
import { useHeatStyleColumnFacets, HEAT_STYLE_FACET_PAGE_CAP } from '../technical/heatStyleColumnFacets';
import './ShipmentDashBoardPage.css';

const { Option } = Select;

const SHIPMENT_FACET_KEYS = [
  'shipmentTypeName',
  'shipmentNumber',
  '__priorityLabel',
  '__categoryLabel',
  '__subcategoryLabel',
  '__psiLabel',
  '__sgrLabel',
];

const SHIPMENT_COLUMN_FILTERS_EMPTY = SHIPMENT_FACET_KEYS.reduce((acc, k) => {
  acc[k] = '';
  return acc;
}, {});

function enrichShipmentRowForFacets(record) {
  if (!record || typeof record !== 'object') return record;
  const meta = normalizeShipmentMetaFromRecord(record);
  const pr = SHIPMENT_PRIORITY_OPTIONS.find((o) => o.value === meta.priority);
  const cat = SHIPMENT_CATEGORY_OPTIONS.find((o) => o.value === meta.shipmentCategory);
  const rawPk =
    record.packagingType ??
    record.packaging_type ??
    record.packagingSubcategory ??
    record.packaging_subcategory;
  let subLabel = '—';
  if (meta.packagingSubcategory) {
    const opt = SHIPMENT_PACKAGING_OPTIONS.find((o) => o.value === meta.packagingSubcategory);
    subLabel = opt ? opt.label : String(rawPk ?? '—');
  } else if (rawPk != null && rawPk !== '') {
    subLabel = String(rawPk);
  }
  const hasPsi =
    record.submissions && record.submissions.some((item) => item.formType === 'Pre-Shipment Inspection');
  const hasSgr =
    record.submissions && record.submissions.some((item) => item.formType === 'Site Goods Received');
  return {
    ...record,
    __priorityLabel: pr ? pr.label : String(record?.priority ?? record?.Priority ?? '—'),
    __categoryLabel: cat ? cat.label : String(record?.shipmentCategory ?? '—'),
    __subcategoryLabel: subLabel,
    __psiLabel: hasPsi ? 'Submitted' : 'Not submitted',
    __sgrLabel: hasSgr ? 'Submitted' : 'Not submitted',
    __psiSort: hasPsi ? 1 : 0,
    __sgrSort: hasSgr ? 1 : 0,
  };
}

const resolveUserGroups = (userAccessList) => {
  const fromProps = Array.isArray(userAccessList) ? userAccessList : [];
  if (fromProps.length > 0) return fromProps;
  try {
    const raw = localStorage.getItem('UserGroup');
    if (raw == null || raw === '') return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return String(raw)
        .split(',')
        .map((s) => s.trim())
        .filter(Boolean);
    }
  } catch {
    return [];
  }
};

const isGlobalAdminGroup = (groups) =>
  Array.isArray(groups) &&
  groups.some((g) => {
    const n = String(g || '').toLowerCase();
    return n === 'admin' || n === 'super-admin' || n === 'administrator';
  });

/** Explicit false => no write; missing tab key => allow (legacy). */
const hasShipmentTabWrite = (projectTeamMembersAccessList) => {
  const tabs = projectTeamMembersAccessList?.[0]?.tabPermissions;
  if (!tabs || typeof tabs !== 'object') return true;
  const w = tabs.Shipment?.write ?? tabs['Shipment']?.write ?? tabs.shipment?.write;
  if (w === false) return false;
  return true;
};

const ShipmentDashBoardPage = (props) => {
  const {
    selectedProject,
    onGetShipmentTypes,
    shipmentsTypes,
    shipmentTypesLoading,
    shipmentTypesCount,
    shipmentData,
    shipmentDataLoading,
    shipmentDataCount,
    OnGetShipmentsData,
    userAccessList,
    projectTeamMembersAccessList,
  } = props;

  const [projectIdValue, setProjectIdValue] = useState(false);

  const [showAddModel, setShowAddModel] = useState(false);
  const [isTableModelVisible, setIsTableModelVisible] = useState(false);
  const [editRecord, setEditRecord] = useState('');
  const [isEdit, setIsEdit] = useState('');
  const [adminShipmentTypesList, setAdminShipmentTypesList] = useState([]);

  const [currentPagesize, setCurrentPagesize] = useState(10);
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false);
  const [isFileModalVisible, setIsFileModalVisible] = useState(false);
  const [fileList, setFileList] = useState([]);
  const [uploadLoading, setUploadLoading] = useState(false);
  const [bulkUploadModalVisible, setBulkUploadModalVisible] = useState(false);
  const [bulkFileList, setBulkFileList] = useState([]);
  const [bulkUploadLoading, setBulkUploadLoading] = useState(false);

  const [deleteLoading, setDeleteLoading] = useState(false);
  const [selectedData, setSelectedData] = useState('');
  const [searchKeyExist, setSearchKeyExist] = useState('');
  const [heatColumnFilters, setHeatColumnFilters] = useState(() => ({ ...SHIPMENT_COLUMN_FILTERS_EMPTY }));
  const [shipmentSort, setShipmentSort] = useState({
    key: 'shipmentTypeName',
    desc: false,
  });

  const [showFormModel, setShowFormModel] = useState(false);

  const [current, setCurrent] = useState(1);

  useEffect(() => {
    if (adminShipmentTypesList && adminShipmentTypesList.length == 0) onGetShipmentTypes();
  }, []);

  useEffect(() => {
    // console.log('ShipmentTypes:', shipmentsTypes.length);

    if (shipmentsTypes && shipmentsTypes.length > 0) setAdminShipmentTypesList(shipmentsTypes);
  }, [shipmentsTypes]);

  useEffect(() => {
    const pid = selectedProject?.id;
    if (!pid) return;
    setProjectIdValue(pid);
    setCurrent(1);
    setSearchKeyExist('');
    setHeatColumnFilters({ ...SHIPMENT_COLUMN_FILTERS_EMPTY });
    setShipmentSort({ key: 'shipmentTypeName', desc: false });
    OnGetShipmentsData({
      projectId: pid,
      pageNumber: 1,
      pageSize: HEAT_STYLE_FACET_PAGE_CAP,
    });
  }, [selectedProject?.id]);

  /** true = user cannot edit/delete shipments */
  const isShipmentReadOnly = useCallback(() => {
    const groups = resolveUserGroups(userAccessList);
    if (isGlobalAdminGroup(groups)) return false;
    if (projectTeamMembersAccessList?.[0]?.isGuestUser) return true;
    if (
      projectTeamMembersAccessList?.[0]?.isNormalUser &&
      !hasShipmentTabWrite(projectTeamMembersAccessList)
    ) {
      return true;
    }
    return false;
  }, [userAccessList, projectTeamMembersAccessList]);

  const renderStatusDot = (record, formType) => {
    const isActive =
      record.submissions && record.submissions.some((item) => item.formType === formType);

    return (
      <Tooltip title={isActive ? 'Form submitted' : 'Form not submitted'}>
        <span className={`ship-status-dot ${isActive ? 'is-success' : 'is-danger'}`}>
          <span className="ship-status-dot-inner" aria-hidden />
        </span>
      </Tooltip>
    );
  };

  const shipmentSearchFiltered = useMemo(() => {
    const rows = (shipmentData || []).map((r) => enrichShipmentRowForFacets({ ...r }));
    const q = searchKeyExist.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((r) => {
      const blob = [
        r.shipmentTypeName,
        r.shipmentNumber,
        r.__priorityLabel,
        r.__categoryLabel,
        r.__subcategoryLabel,
        r.__psiLabel,
        r.__sgrLabel,
      ]
        .map((x) => (x == null ? '' : String(x)).toLowerCase())
        .join(' ');
      return blob.includes(q);
    });
  }, [shipmentData, searchKeyExist]);

  const handleShipmentHeatFilterChange = useCallback((key, value) => {
    setHeatColumnFilters((prev) => ({ ...prev, [key]: value }));
    setCurrent(1);
  }, []);

  const shipmentFacetRef = useRef([]);
  shipmentFacetRef.current = shipmentSearchFiltered;
  const fetchShipmentFacetRows = useCallback(async () => {
    const list = shipmentFacetRef.current;
    return Array.isArray(list) ? [...list] : [];
  }, []);

  const shipmentFacetSearchCriteria = useMemo(() => ({}), []);

  const { applyFacetClientFilter, renderTextColumnFilterTrigger } = useHeatStyleColumnFacets({
    enabled: Boolean(projectIdValue),
    filterableColumns: SHIPMENT_FACET_KEYS,
    listCount: shipmentSearchFiltered.length,
    projectList: shipmentSearchFiltered,
    searchCriteria: shipmentFacetSearchCriteria,
    columnFilters: heatColumnFilters,
    onColumnFilterChange: handleShipmentHeatFilterChange,
    fetchFacetRows: fetchShipmentFacetRows,
  });

  const lcShip = (v) => (v == null ? '' : String(v)).trim().toLowerCase();

  const filteredAndFacetedShipments = useMemo(() => {
    let list = [...shipmentSearchFiltered];
    SHIPMENT_FACET_KEYS.forEach((key) => {
      const raw = (heatColumnFilters[key] || '').trim();
      if (!raw) return;
      const q = lcShip(raw);
      list = list.filter((row) => lcShip(get(row, key)).includes(q));
    });
    return applyFacetClientFilter(list);
  }, [shipmentSearchFiltered, heatColumnFilters, applyFacetClientFilter]);

  const handleShipmentSortClick = useCallback((field) => {
    setShipmentSort((prev) =>
      prev.key === field ? { ...prev, desc: !prev.desc } : { key: field, desc: false }
    );
  }, []);

  const renderShipmentSortCaret = useCallback(
    (field) =>
      shipmentSort.key === field ? (
        shipmentSort.desc ? (
          <CaretDownOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        ) : (
          <CaretUpOutlined className="equipment-type-sort-dir-icon" aria-hidden />
        )
      ) : (
        <CaretUpOutlined
          className="equipment-type-sort-dir-icon doc-control-sort-caret-inactive"
          aria-hidden
        />
      ),
    [shipmentSort.key, shipmentSort.desc]
  );

  const renderShipmentHeader = useCallback(
    (labelNode, dataIndex, sortField, { filterable = true, headerAlign = 'center' } = {}) => {
      if (!filterable) {
        return (
          <div className={`dpr-pipelines-header-static dpr-pipelines-header-static--${headerAlign}`}>
            {labelNode}
          </div>
        );
      }
      return (
        <div className={`dpr-pipelines-heat-header dpr-pipelines-heat-header--${headerAlign}`}>
          <div className="column-header-text-wrapper spares-column-header-sortable dpr-pipelines-heat-title-row">
            <span
              className="dpr-pipelines-heat-label dpr-pipelines-heat-label--sortable"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                handleShipmentSortClick(sortField);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleShipmentSortClick(sortField);
                }
              }}
            >
              {labelNode}
            </span>
            <span
              className="custom-sort-icon"
              role="button"
              tabIndex={0}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleShipmentSortClick(sortField);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleShipmentSortClick(sortField);
                }
              }}
            >
              {renderShipmentSortCaret(sortField)}
            </span>
            {renderTextColumnFilterTrigger(dataIndex)}
          </div>
        </div>
      );
    },
    [handleShipmentSortClick, renderShipmentSortCaret, renderTextColumnFilterTrigger]
  );

  const sortedShipmentDisplayList = useMemo(() => {
    const list = [...filteredAndFacetedShipments];
    const cmpStr = (a, b, key) =>
      String(get(a, key) ?? '')
        .trim()
        .toLowerCase()
        .localeCompare(String(get(b, key) ?? '').trim().toLowerCase(), undefined, {
          sensitivity: 'base',
          numeric: true,
        });
    const cmpNum = (a, b, key) => {
      const na = Number(get(a, key));
      const nb = Number(get(b, key));
      if (!Number.isFinite(na) && !Number.isFinite(nb)) return 0;
      if (!Number.isFinite(na)) return 1;
      if (!Number.isFinite(nb)) return -1;
      return na - nb;
    };
    const tieBreak = (a, b) => {
      const idA = a?.id != null ? Number(a.id) : NaN;
      const idB = b?.id != null ? Number(b.id) : NaN;
      if (Number.isFinite(idA) && Number.isFinite(idB) && idA !== idB) {
        return idA < idB ? -1 : 1;
      }
      return String(a?.pihShipmentId ?? a?.id ?? '').localeCompare(
        String(b?.pihShipmentId ?? b?.id ?? ''),
        undefined,
        { numeric: true }
      );
    };
    const { key: sortKey, desc: sortDesc } = shipmentSort;
    list.sort((a, b) => {
      let cmp = 0;
      switch (sortKey) {
        case 'shipmentTypeName':
        case 'shipmentNumber':
        case '__priorityLabel':
        case '__categoryLabel':
        case '__subcategoryLabel':
        case '__psiLabel':
        case '__sgrLabel':
          cmp = cmpStr(a, b, sortKey);
          break;
        case '__psiSort':
        case '__sgrSort':
          cmp = cmpNum(a, b, sortKey);
          break;
        default:
          cmp = 0;
      }
      if (cmp === 0) cmp = tieBreak(a, b);
      return sortDesc ? -cmp : cmp;
    });
    return list;
  }, [filteredAndFacetedShipments, shipmentSort]);

  const shipmentPagedRows = useMemo(() => {
    const start = (current - 1) * currentPagesize;
    return sortedShipmentDisplayList.slice(start, start + currentPagesize);
  }, [sortedShipmentDisplayList, current, currentPagesize]);

  const onShowEditModel = (record) => {
    setEditRecord(record);
    setIsEdit(true);
    setShowAddModel(true);
  };

  const onUpdateFileClick = (data) => {
    if (data) {
      setSelectedData(data);
      setIsFileModalVisible(true);
    }
  };

  const showDeleteModal = (record) => {
    setSelectedData(record);
    setIsDeleteModalVisible(true);
  };

  const handleEyeClick = async (record) => {
    setSelectedData(record);
    setIsTableModelVisible(true);
    setShowFormModel(true);
  };

  /** Setting coloumn (headers) for Shipment table */
  const columns = useMemo(
    () => [
      {
        title: renderShipmentHeader('#', null, null, { filterable: false, headerAlign: 'center' }),
        dataIndex: 'sno',
        key: 'sno',
        align: 'center',
        width: 48,
        render: (text, record, index) => {
          return (current - 1) * currentPagesize + (index + 1);
        },
      },
      {
        title: renderShipmentHeader(
          'Shipment Type',
          'shipmentTypeName',
          'shipmentTypeName',
          { headerAlign: 'left' }
        ),
        dataIndex: 'shipmentTypeName',
        key: 'shipmentTypeName',
        align: 'left',
        width: 200,
        ellipsis: {
          showTitle: true,
        },
      },

      {
        title: renderShipmentHeader(
          'Shipment Number',
          'shipmentNumber',
          'shipmentNumber',
          { headerAlign: 'center' }
        ),
        dataIndex: 'shipmentNumber',
        key: 'shipmentNumber',
        align: 'center',
        width: 168,
      },
      {
        title: renderShipmentHeader('Priority', '__priorityLabel', '__priorityLabel'),
        key: 'priority',
        dataIndex: 'priority',
        width: 118,
        align: 'center',
        ellipsis: { showTitle: true },
        render: (_, record) => {
          const r = enrichShipmentRowForFacets(record);
          return r.__priorityLabel;
        },
      },
      {
        title: renderShipmentHeader('Category', '__categoryLabel', '__categoryLabel'),
        key: 'shipmentCategory',
        dataIndex: 'shipmentCategory',
        width: 128,
        align: 'center',
        ellipsis: { showTitle: true },
        render: (_, record) => {
          const r = enrichShipmentRowForFacets(record);
          return r.__categoryLabel;
        },
      },
      {
        title: renderShipmentHeader(
          'Subcategory',
          '__subcategoryLabel',
          '__subcategoryLabel',
          { headerAlign: 'center' }
        ),
        key: 'packagingSubcategory',
        dataIndex: 'packagingType',
        width: 172,
        align: 'center',
        ellipsis: { showTitle: true },
        render: (_, record) => {
          const r = enrichShipmentRowForFacets(record);
          return r.__subcategoryLabel;
        },
      },
      {
        title: renderShipmentHeader(
          <Tooltip title="Pre-Shipment Inspection form status">
            <span className="ship-th-with-hint">PSI</span>
          </Tooltip>,
          '__psiLabel',
          '__psiSort'
        ),
        key: 'psi',
        dataIndex: 'psi',
        width: 104,
        render: (_, record) => renderStatusDot(record, 'Pre-Shipment Inspection'),
        align: 'center',
        onHeaderCell: () => ({ className: 'shipment-th-status' }),
      },
      {
        title: renderShipmentHeader(
          <Tooltip title="Site Goods Received form status">
            <span className="ship-th-with-hint">SGR</span>
          </Tooltip>,
          '__sgrLabel',
          '__sgrSort'
        ),
        key: 'sgr',
        dataIndex: 'sgr',
        width: 104,
        render: (_, record) => renderStatusDot(record, 'Site Goods Received'),
        align: 'center',
        onHeaderCell: () => ({ className: 'shipment-th-status' }),
      },
      {
        title: renderShipmentHeader('Actions', null, null, {
          filterable: false,
          headerAlign: 'center',
        }),
        key: 'action',
        dataIndex: 'action',
        width: 220,
        fixed: 'right',
        render: (text, record) => {
          const readOnly = isShipmentReadOnly();
          return (
            <div className="ship-actions-cell">
              <Tooltip title="View shipment">
                <button
                  type="button"
                  className="ship-action-btn"
                  aria-label="View shipment"
                  onClick={() => handleEyeClick(record)}
                >
                  <Eye size={18} strokeWidth={1.75} />
                </button>
              </Tooltip>
              <Tooltip title="Edit">
                <button
                  type="button"
                  className="ship-action-btn"
                  aria-label="Edit shipment"
                  disabled={readOnly}
                  onClick={() => {
                    if (!readOnly) onShowEditModel(record);
                  }}
                >
                  <Pencil size={18} strokeWidth={1.75} />
                </button>
              </Tooltip>
              <Tooltip title="Delete">
                <button
                  type="button"
                  className="ship-action-btn ship-action-btn--delete"
                  aria-label="Delete shipment"
                  disabled={readOnly}
                  onClick={() => {
                    if (!readOnly) showDeleteModal(record);
                  }}
                >
                  <Trash2 size={18} strokeWidth={1.75} />
                </button>
              </Tooltip>
              <Tooltip title="Documents">
                <button
                  type="button"
                  className="ship-action-btn"
                  aria-label="Shipment documents"
                  onClick={() => onUpdateFileClick(record)}
                >
                  <FileText size={18} strokeWidth={1.75} />
                </button>
              </Tooltip>
            </div>
          );
        },
        align: 'center',
        onHeaderCell: () => ({ className: 'shipment-th-actions' }),
        onCell: () => ({ className: 'shipment-td-actions' }),
      },
    ],
    [
      isShipmentReadOnly,
      current,
      currentPagesize,
      renderShipmentHeader,
      renderStatusDot,
      handleEyeClick,
      onShowEditModel,
      showDeleteModal,
      onUpdateFileClick,
    ]
  );

  const columnsFile = [
    {
      title: 'File Name',
      dataIndex: 'fileName',
      key: 'fileName',
      align: 'left',
      render: (text) => (
        <div style={{ display: 'flex', alignItems: 'left', height: '100%' }}>{text}</div>
      ),
    },
    {
      title: 'Action',
      dataIndex: 'edit_delete',
      key: 'edit_delete',
      align: 'center',
      render: (text, record) => {
        return (
          <div>
            <Tooltip title="Download">
              <a
                href={`${AWS_IMAGE_URL}/${record.fileKey}`}
                className="shipment-download-link"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Download file"
              >
                <Download size={18} strokeWidth={2} />
              </a>
            </Tooltip>
          </div>
        );
      },
    },
  ];

  const OnfetchData = async (page = 1, size = HEAT_STYLE_FACET_PAGE_CAP) => {
    setCurrent(page);
    const pid = selectedProject?.id;
    if (!pid) return;
    setProjectIdValue(pid);
    const obj = {
      pageSize: size,
      pageNumber: page,
      projectId: pid,
    };
    await OnGetShipmentsData(obj);
  };

  const handleOk = async () => {
    setDeleteLoading(true);
    await OnDeleteShipments(selectedData);
    await OnfetchData();
    setDeleteLoading(false);
    setIsDeleteModalVisible(false);
  };

  const handleCancel = () => {
    setIsDeleteModalVisible(false);
    setSelectedData('');
  };

  const handleBulkUploadCancel = () => {
    setBulkUploadModalVisible(false);
    setBulkFileList([]);
  };

  /** Close Modal on close of table details */
  const handleCancelTable = () => {
    setIsTableModelVisible(false);
    setShowFormModel(false);
  };

  /** Toolbar search only — same as Pipelines / Equipment (client-side on loaded rows). */
  const onSearchEquip = (event) => {
    setSearchKeyExist(event.target.value);
    setCurrent(1);
  };

  const toAntUploadFile = (file) => ({
    uid: file.uid || `file-${file.name}-${Date.now()}`,
    name: file.name,
    status: 'done',
    originFileObj: file.originFileObj != null ? file.originFileObj : file,
  });

  const getRawFile = (item) => item?.originFileObj || item;

  // File upload functions
  const fileProps = {
    multiple: true,
    fileList,
    beforeUpload: (file) => {
      setFileList((prev) => [...prev, toAntUploadFile(file)]);
      return false; // Prevent automatic upload
    },
    onRemove: (file) => {
      setFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    },
    accept: '.xlsx,.xls,.pdf',
    maxCount: 100,
  };

  // Bulk upload file props
  const bulkFileProps = {
    multiple: true,
    fileList: bulkFileList,
    beforeUpload: (file) => {
      setBulkFileList((prev) => [...prev, toAntUploadFile(file)]);
      return false; // Prevent automatic upload
    },
    onRemove: (file) => {
      setBulkFileList((prev) => prev.filter((item) => item.uid !== file.uid));
    },
    accept: '.xlsx,.xls,.pdf',
    maxCount: 100,
  };

  const uploadFilesToS3 = async () => {
    try {
      const uploadedFiles = [];

      if (fileList && fileList.length > 0) {
        const fileObjs = fileList.map((item) => {
          const file = getRawFile(item);
          return {
            fileName: file.name.replace(/\(\d+\)/g, '').trim(),
            contentType: file.type,
          };
        });

        // Get signed URLs for multiple files using the shipment-specific endpoint
        const signedUrlRequests = fileObjs.map((fileObj) =>
          axiosConfig.get(
            `/get-shipment-pre-signed-url?fileName=${encodeURIComponent(
              fileObj.fileName
            )}&contentType=${encodeURIComponent(fileObj.contentType)}`
          )
        );

        const signedUrlResponses = await Promise.all(signedUrlRequests);

        if (signedUrlResponses.every((res) => res.status === 200)) {
          for (let i = 0; i < signedUrlResponses.length; i++) {
            const data = signedUrlResponses[i].data.data;
            const file = getRawFile(fileList[i]);

            if (file && data) {
              try {
                const uploadResponse = await axios.put(data.presignedUrl, file, {
                  headers: {
                    'Content-Disposition': 'attachment',
                    'Content-Type': file.type || 'application/octet-stream',
                  },
                });

                if (uploadResponse.status === 200) {
                  uploadedFiles.push({
                    fileKey: data.fileKey,
                    fileName: data.fileName,
                  });
                } else {
                  console.error(`Error uploading file: ${file.name}`, uploadResponse);
                }
              } catch (error) {
                console.error(`Error processing file: ${file.name}`, error);
              }
            }
          }
        }

        setFileList([]);
      }
      return uploadedFiles;
    } catch (error) {
      console.error('Error uploading files:', error);
      return []; // Return empty array on error to prevent "not iterable" error
    }
  };

  const handleUploadFiles = async () => {
    if (fileList.length === 0) {
      notification.warning({
        message: 'Please select files to upload.',
      });
      return;
    }

    setUploadLoading(true);
    try {
      const uploadedFiles = await uploadFilesToS3();

      if (uploadedFiles.length > 0) {
        // No transformation needed, uploadedFiles already has fileKey and fileName
        let existingDocuments = selectedData.document || [];
        const newDocuments = [...existingDocuments, ...uploadedFiles];

        const updateData = {
          id: selectedData.pihShipmentId,
          data: {
            document: newDocuments,
            // Only pass document field, keep other fields unchanged
          },
        };

        // Import the update function
        const { OnAddorUpdateShipmentsDashboard } = await import(
          '../../redux/ShipmentDashboardDetails/saga'
        );
        const res = await OnAddorUpdateShipmentsDashboard(updateData, true);

        if (res) {
          notification.success({
            message: 'Files uploaded successfully.',
          });
          await OnfetchData();
          setIsFileModalVisible(false);
        } else {
          notification.error({
            message: 'Failed to update files.',
          });
        }
      }
    } catch (error) {
      notification.error({
        message: 'Error uploading files.',
        description: error.message,
      });
    } finally {
      setUploadLoading(false);
    }
  };

  // Bulk upload function for shipment documents (per-file results; avoids silent failure on 404 etc.)
  const uploadBulkFilesToS3 = async () => {
    const uploadedFiles = [];
    const failures = [];

    if (!bulkFileList || bulkFileList.length === 0) {
      return { uploadedFiles, failures };
    }

    const fileObjs = bulkFileList.map((item) => {
      const file = getRawFile(item);
      return {
        fileName: file.name.replace(/\(\d+\)/g, '').trim(),
        contentType: file.type,
      };
    });

    const settled = await Promise.allSettled(
      fileObjs.map((fileObj) =>
        axiosConfig.get(
          `/get-shipment-pre-signed-url?fileName=${encodeURIComponent(
            fileObj.fileName
          )}&contentType=${encodeURIComponent(fileObj.contentType)}`
        )
      )
    );

    for (let i = 0; i < settled.length; i++) {
      const result = settled[i];
      const file = getRawFile(bulkFileList[i]);
      const displayName = file?.name ?? fileObjs[i]?.fileName ?? `File ${i + 1}`;

      if (result.status === 'rejected') {
        const err = result.reason;
        const status = err?.response?.status;
        const detail =
          err?.response?.data?.message ||
          err?.message ||
          (status ? `Request failed (${status})` : 'Could not get upload URL');
        failures.push({ fileName: displayName, reason: detail });
        continue;
      }

      const res = result.value;
      if (res.status !== 200 || !res.data?.data) {
        failures.push({
          fileName: displayName,
          reason: res.status ? `Server returned ${res.status}` : 'Invalid server response',
        });
        continue;
      }

      const data = res.data.data;

      if (!file || !data.presignedUrl) {
        failures.push({
          fileName: displayName,
          reason: 'Missing upload URL in server response',
        });
        continue;
      }

      try {
        const uploadResponse = await axios.put(data.presignedUrl, file, {
          headers: {
            'Content-Disposition': 'attachment',
            'Content-Type': file.type || 'application/octet-stream',
          },
        });

        if (uploadResponse.status === 200) {
          uploadedFiles.push({
            fileKey: data.fileKey,
            fileName: data.fileName,
          });
        } else {
          failures.push({
            fileName: displayName,
            reason: `Upload to storage failed (${uploadResponse.status})`,
          });
        }
      } catch (error) {
        failures.push({
          fileName: displayName,
          reason: error.message || 'Upload to storage failed',
        });
      }
    }

    return { uploadedFiles, failures };
  };

  const handleBulkUploadFiles = async () => {
    if (bulkFileList.length === 0) {
      notification.warning({
        message: 'Please select files to upload.',
      });
      return;
    }

    setBulkUploadLoading(true);
    try {
      const { uploadedFiles, failures } = await uploadBulkFilesToS3();

      const failureSummary = (items) =>
        items
          .slice(0, 5)
          .map((f) => `${f.fileName}: ${f.reason}`)
          .join('\n') + (items.length > 5 ? `\n… and ${items.length - 5} more` : '');

      if (uploadedFiles.length > 0) {
        setBulkFileList([]);
        if (failures.length === 0) {
          notification.success({
            message: `${uploadedFiles.length} file${uploadedFiles.length === 1 ? '' : 's'} uploaded successfully.`,
          });
        } else {
          notification.warning({
            message: `${uploadedFiles.length} file${uploadedFiles.length === 1 ? '' : 's'} uploaded; ${failures.length} failed`,
            description: failureSummary(failures),
          });
        }
        setBulkUploadModalVisible(false);
        await OnfetchData();
      } else if (failures.length > 0) {
        notification.error({
          message: 'Bulk upload failed',
          description: failureSummary(failures),
        });
      } else {
        notification.error({
          message: 'Bulk upload failed',
          description: 'No files were uploaded. Please try again.',
        });
      }
    } catch (error) {
      notification.error({
        message: 'Error uploading files.',
        description: error.message,
      });
    } finally {
      setBulkUploadLoading(false);
    }
  };

  const readOnly = isShipmentReadOnly();

  return (
    <div className="modern-shipment-page">
      <div className="ship-header">
        <div className="ship-header-left">
          <div className="ship-header-icon">
            <Truck size={24} color="#2563eb" />
          </div>
          <div className="ship-header-titles">
            <h1 className="ship-title">Shipments</h1>
            <p className="ship-subtitle">List and manage shipment numbers for this project.</p>
          </div>
        </div>
        <div className="ship-header-actions">
          <CrceButton
            label="Bulk upload documents"
            variant="tertiary"
            size="small"
            icon={UploadIcon}
            onClick={() => !readOnly && setBulkUploadModalVisible(true)}
            disabled={readOnly}
          />
          <CrceButton
            label="Add new"
            variant="primary"
            size="small"
            icon={Plus}
            onClick={() => {
              if (!readOnly) {
                setEditRecord('');
                setIsEdit(false);
                setShowAddModel(true);
              }
            }}
            disabled={readOnly}
          />
        </div>
      </div>

      <div className="ship-toolbar">
        <div className="ship-search-wrap">
          <Search size={16} className="ship-search-icon" />
          <input
            type="text"
            className="ship-search-input"
            placeholder="Search shipments…"
            value={searchKeyExist}
            onChange={onSearchEquip}
            aria-label="Search shipments"
          />
        </div>
      </div>

      <div className="ship-table-shell dpr-settings-pipelines-table-wrap">
        <Table
          columns={columns}
          dataSource={shipmentPagedRows}
          className="shipment-dashboard-table project-tab-heat-table"
          rowKey={(r) => r.pihShipmentId ?? r.id ?? r.shipmentNumber}
          scroll={{ x: 1328 }}
          pagination={
            sortedShipmentDisplayList.length > 0
              ? {
                  position: ['bottomCenter'],
                  className: 'heat-profile-pagination',
                  showSizeChanger: true,
                  pageSizeOptions: ['10', '20', '50', '100'],
                  showTotal: (total, range) => `${range[0]}–${range[1]} of ${total} shipments`,
                  total: sortedShipmentDisplayList.length,
                  pageSize: currentPagesize,
                  current,

                  onChange: (pageNumber, pageSize) => {
                    setCurrent(pageNumber);
                    setCurrentPagesize(pageSize);
                  },
                }
              : false
          }
          loading={getProjectTableLoadingConfig(shipmentDataLoading)}
          locale={{ emptyText: <ProjectTabTableEmpty /> }}
        />
      </div>

      {showAddModel && (
        <AddNewShipments
          showAddModel={showAddModel}
          setShowAddModel={setShowAddModel}
          setIsEdit={setIsEdit}
          setEditRecord={setEditRecord}
          editRecord={editRecord}
          isEdit={isEdit}
          OnfetchData={OnfetchData}
          selectedProject={selectedProject}
          adminShipmentTypesList={adminShipmentTypesList}
        />
      )}

      {showFormModel && (
        <ShipmentLogs
          showFormModel={showFormModel}
          setShowFormModel={setShowFormModel}
          selectedData={selectedData}
        />
      )}

      {isDeleteModalVisible &&
        selectedData &&
        typeof selectedData === 'object' &&
        (selectedData.pihShipmentId != null || selectedData.id != null) && (
          <div
            className="mc-dpr-modal-backdrop"
            role="presentation"
            onClick={() => !deleteLoading && handleCancel()}
          >
            <div
              className="mc-dpr-modal mc-dpr-modal--narrow mc-dpr-modal--delete"
              role="dialog"
              aria-labelledby="ship-delete-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mc-dpr-modal-head">
                <h3 id="ship-delete-title" className="mc-dpr-modal-title">
                  Delete shipment
                </h3>
                <button
                  type="button"
                  className="mc-dpr-modal-close"
                  aria-label="Close"
                  disabled={deleteLoading}
                  onClick={handleCancel}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="mc-dpr-modal-body">
                <div className="mc-dpr-modal-delete-body">
                  <p className="mc-dpr-modal-delete-lead">
                    Deleting a shipment removes its shipment number and related information.
                  </p>
                  <p className="mc-dpr-modal-delete-target">
                    {selectedData.shipmentTypeName} — {selectedData.shipmentNumber}
                  </p>
                  <p className="mc-dpr-modal-delete-warning">This action cannot be undone.</p>
                </div>
              </div>
              <div className="mc-dpr-modal-foot">
                <CrceButton
                  label="Cancel"
                  variant="tertiary"
                  size="medium"
                  onClick={handleCancel}
                  disabled={deleteLoading}
                />
                <span className="mc-delete-confirm-wrap">
                  <CrceButton
                    label={deleteLoading ? 'Deleting…' : 'Delete'}
                    variant="primary"
                    size="medium"
                    onClick={handleOk}
                    disabled={deleteLoading}
                  />
                </span>
              </div>
            </div>
          </div>
        )}

      {isFileModalVisible &&
        selectedData &&
        typeof selectedData === 'object' && (
          <div className="mc-dpr-modal-backdrop" role="presentation">
            <div
              className="mc-dpr-modal mc-dpr-modal--large"
              role="dialog"
              aria-labelledby="ship-files-title"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mc-dpr-modal-head">
                <h3 id="ship-files-title" className="mc-dpr-modal-title">
                  Shipment documents
                </h3>
                <button
                  type="button"
                  className="mc-dpr-modal-close"
                  aria-label="Close"
                  disabled={uploadLoading}
                  onClick={() => {
                    setIsFileModalVisible(false);
                    setFileList([]);
                  }}
                >
                  <X size={20} />
                </button>
              </div>
              <div className="mc-dpr-modal-body mc-dpr-modal-files-body">
                <Table
                  columns={columnsFile}
                  dataSource={selectedData.document || []}
                  pagination={false}
                  className="shipment-inner-file-table"
                  locale={{ emptyText: <ProjectTabTableEmpty /> }}
                />
                <div className="mc-dpr-modal-intro" style={{ marginTop: '20px' }}>
                  <strong style={{ color: '#374151' }}>Add files</strong>
                  <br />
                  Excel or PDF, up to 100 files.
                </div>
                <div className="ship-modal-upload-wrap">
                  <Upload {...fileProps} className="ship-modal-upload">
                    <CrceButton
                      label="Select files"
                      variant="tertiary"
                      size="medium"
                      icon={UploadIcon}
                      disabled={fileList.length >= 100 || uploadLoading}
                    />
                  </Upload>
                </div>
                {fileList.length > 0 && (
                  <div className="ship-upload-toolbar">
                    <CrceButton
                      label={uploadLoading ? 'Uploading…' : 'Upload files'}
                      variant="primary"
                      size="medium"
                      onClick={handleUploadFiles}
                      disabled={uploadLoading}
                    />
                    <CrceButton
                      label="Clear selection"
                      variant="tertiary"
                      size="medium"
                      onClick={() => setFileList([])}
                      disabled={uploadLoading}
                    />
                    <span style={{ fontSize: 13, color: '#64748b' }}>
                      {fileList.length} file(s) selected
                    </span>
                  </div>
                )}
              </div>
              <div className="mc-dpr-modal-foot">
                <CrceButton
                  label="Close"
                  variant="tertiary"
                  size="medium"
                  onClick={() => {
                    setIsFileModalVisible(false);
                    setFileList([]);
                  }}
                  disabled={uploadLoading}
                />
              </div>
            </div>
          </div>
        )}

      {bulkUploadModalVisible && (
        <div
          className="mc-dpr-modal-backdrop"
          role="presentation"
          onClick={() => !bulkUploadLoading && handleBulkUploadCancel()}
        >
          <div
            className="mc-dpr-modal mc-dpr-modal--medium ship-bulk-modal"
            role="dialog"
            aria-labelledby="ship-bulk-title"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mc-dpr-modal-head">
              <h3 id="ship-bulk-title" className="mc-dpr-modal-title">
                Bulk upload documents
              </h3>
              <button
                type="button"
                className="mc-dpr-modal-close"
                aria-label="Close"
                disabled={bulkUploadLoading}
                onClick={handleBulkUploadCancel}
              >
                <X size={20} />
              </button>
            </div>
            <div className="mc-dpr-modal-body ship-bulk-modal-body">
              <p className="mc-dpr-modal-intro">
                Select multiple Excel or PDF files (up to 100). They will be stored as shipment
                documents.
              </p>
              <div className="mc-dpr-modal-upload-wrap ship-modal-upload-wrap">
                <Upload {...bulkFileProps} className="ship-modal-upload">
                  <CrceButton
                    label="Select files"
                    variant="tertiary"
                    size="medium"
                    icon={UploadIcon}
                    disabled={bulkFileList.length >= 100 || bulkUploadLoading}
                  />
                </Upload>
              </div>
              {bulkFileList.length > 0 && (
                <div className="ship-bulk-selected-summary" role="status">
                  {bulkFileList.length} file{bulkFileList.length === 1 ? '' : 's'} selected
                </div>
              )}
            </div>
            <div className="mc-dpr-modal-foot">
              <CrceButton
                label="Cancel"
                variant="tertiary"
                size="medium"
                onClick={handleBulkUploadCancel}
                disabled={bulkUploadLoading}
              />
              <CrceButton
                label={bulkUploadLoading ? 'Uploading…' : 'Upload files'}
                variant="primary"
                size="medium"
                onClick={handleBulkUploadFiles}
                disabled={bulkUploadLoading || bulkFileList.length === 0}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const mapStateToProps = ({ ShipmentReducer, ShipmentDashBoardReducer, equip }) => {
  const shipmentsTypes = get(ShipmentReducer, 'shipmentData', []);
  const shipmentTypesLoading = get(ShipmentReducer, 'isShipmentTypeLoading', false);
  const shipmentTypesCount = get(ShipmentReducer, 'isShipmentTypeLoading', false);

  const shipmentData = get(ShipmentDashBoardReducer, 'shipmentListData', []);
  const shipmentDataCount = get(ShipmentDashBoardReducer, 'shipmentsCount', false);
  const shipmentDataLoading = get(ShipmentDashBoardReducer, 'isShipmentsLoading', false);

  const userAccessList = get(equip, 'userAccessList', []);
  const projectTeamMembersAccessList = get(equip, 'projectTeamMembersAccessList', []);

  return {
    shipmentsTypes,
    shipmentTypesLoading,
    shipmentTypesCount,
    shipmentData,
    shipmentDataLoading,
    shipmentDataCount,
    userAccessList,
    projectTeamMembersAccessList,
  };
};

const mapDispatchToProps = {
  onGetShipmentTypes: fetchShipmentTypeDetails,
  OnGetShipmentsData: fetchShipmentDashBoardDetails,
};

export default connect(mapStateToProps, mapDispatchToProps)(ShipmentDashBoardPage);
